import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarOff, Loader2, PartyPopper, Plus, RotateCcw, Sun } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiDelete, apiGet, apiPut } from '@/lib/api';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const fmt = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN',
  { weekday: 'short', day: 'numeric', month: 'short' });

/**
 * Holidays are a family thing. Our festival list is only a suggestion — any of
 * it can be turned back into a study day, and anything can be added.
 */
export function HolidayCalendarDialog({ open, onOpenChange, childId = '' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState({ date: '', label: '' });
  const q = childId ? `?child_id=${encodeURIComponent(childId)}` : '';

  const load = useCallback(() => {
    setLoading(true);
    apiGet(`/premium/v1/calendar${q}`).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [q]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async (patch) => {
    try {
      await apiPut('/premium/v1/calendar', { child_id: childId || null, ...patch });
      load();
    } catch (e) { toast.error(e?.message || 'Could not save that day'); }
  };

  const reset = async (date) => {
    try { await apiDelete(`/premium/v1/calendar/${date}${q}`); load(); }
    catch (e) { toast.error(e?.message || 'Could not reset that day'); }
  };

  const addDay = async () => {
    if (!adding.date) { toast.message('Pick a date first'); return; }
    await save({ date: adding.date, label: adding.label || 'Family holiday', is_holiday: true });
    setAdding({ date: '', label: '' });
  };

  const toggleWeeklyOff = async (idx) => {
    const current = data?.prefs?.weekly_off || [];
    const next = current.includes(idx) ? current.filter(d => d !== idx) : [...current, idx];
    try {
      const res = await apiPut('/premium/v1/study-prefs', {
        child_id: childId || null, daily_minutes: data.prefs.daily_minutes,
        buffer_pct: data.prefs.buffer_pct, weekly_off: next,
        revision_days: data.prefs.revision_days ?? null,
        mocks_enabled: data.prefs.mocks_enabled,
        full_portion_revision: data.prefs.full_portion_revision,
        puzzle_slots: data.prefs.puzzle_slots,
      });
      setData(d => ({ ...d, prefs: res }));
    } catch (e) { toast.error(e?.message || 'Could not change the weekly off day'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="holiday-calendar-dialog">
        <DialogTitle className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
          <PartyPopper className="w-4 h-4 text-amber-500" /> Holidays & study days
        </DialogTitle>
        <DialogDescription className="text-xs text-slate-500 font-body">
          We suggest the usual festivals — keep what your family celebrates, and turn the rest back
          into study days. Daily plans skip whatever is marked off.
        </DialogDescription>

        {loading && <p className="py-6 text-center text-sm text-slate-400 font-body"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</p>}

        {!loading && data && (
          <div className="space-y-4">
            <div data-testid="weekly-off-row">
              <Label className="text-xs font-body text-slate-500">Weekly day off</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {WEEKDAYS.map((d, i) => {
                  const on = (data.prefs?.weekly_off || []).includes(i);
                  return (
                    <button key={d} data-testid={`weekly-off-${i}`} onClick={() => toggleWeeklyOff(i)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-heading transition-colors ${on ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 p-3">
              <Label className="text-xs font-body text-slate-500">Add a day off</Label>
              <div className="flex flex-col sm:flex-row gap-2 mt-1.5">
                <Input type="date" data-testid="holiday-date-input" value={adding.date}
                  onChange={e => setAdding(a => ({ ...a, date: e.target.value }))} className="rounded-lg text-sm" />
                <Input data-testid="holiday-label-input" placeholder="Reason (birthday, wedding…)" value={adding.label}
                  onChange={e => setAdding(a => ({ ...a, label: e.target.value }))} className="rounded-lg text-sm" />
                <Button size="sm" className="h-9 text-[11px] bg-slate-900 text-white" data-testid="holiday-add-btn" onClick={addDay}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add
                </Button>
              </div>
            </div>

            <div className="space-y-1.5" data-testid="holiday-list">
              {(data.entries || []).length === 0 && (
                <p className="text-[12px] text-slate-500 font-body">No days marked yet.</p>
              )}
              {(data.entries || []).map(e => (
                <div key={e.date} data-testid={`holiday-row-${e.date}`}
                  className={`flex items-center gap-2 rounded-2xl border p-2.5 ${e.is_holiday ? 'border-amber-200 bg-amber-50/60' : 'border-emerald-200 bg-emerald-50/60'}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-heading text-[12px] font-semibold text-slate-800">{fmt(e.date)}</p>
                    <p className="text-[11px] text-slate-500 font-body truncate">
                      {e.label || (e.is_holiday ? 'Day off' : 'Study day')}
                      {e.source === 'suggested' && ' · our suggestion'}
                    </p>
                  </div>
                  {e.is_holiday ? (
                    <Button size="sm" variant="outline" className="h-7 text-[10px]" data-testid={`holiday-study-${e.date}`}
                      onClick={() => save({ date: e.date, label: e.label, is_holiday: false })}>
                      <Sun className="w-3 h-3 mr-1" /> Study day
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 text-[10px]" data-testid={`holiday-off-${e.date}`}
                      onClick={() => save({ date: e.date, label: e.label, is_holiday: true })}>
                      <CalendarOff className="w-3 h-3 mr-1" /> Day off
                    </Button>
                  )}
                  {e.source === 'family' && (
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px] text-slate-400" data-testid={`holiday-reset-${e.date}`}
                      onClick={() => reset(e.date)} title="Remove my change">
                      <RotateCcw className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default HolidayCalendarDialog;
